/**
 * cardFraming.js
 *
 * Inquadratura della foto di copertina nella scheda veicolo (CarCard).
 * Il noleggiatore sceglie il punto di messa a fuoco (x, y in %) e lo zoom
 * (1×–2×). Si salva su `cars/{id}/cardFraming` come:
 *   { url, x, y, zoom }
 * dove `url` è la foto di copertina a cui l'inquadratura si riferisce: se la
 * copertina cambia (nuova gallery, "usa come cover") l'inquadratura vecchia
 * viene ignorata automaticamente, perché riferita a un'altra foto.
 *
 * Editor (CardFramingEditor) e scheda pubblica usano ESATTAMENTE queste
 * funzioni e lo stesso rapporto d'aspetto (CARD_IMAGE_ASPECT), così ciò che il
 * noleggiatore vede in anteprima coincide con ciò che vede il cliente.
 */

export const CARD_IMAGE_ASPECT = '16 / 10';
export const MIN_ZOOM = 1;
export const MAX_ZOOM = 2;
// Oltre questo zoom la miniatura (800px) non basta più: si usa la foto intera.
export const FULL_RES_ZOOM = 1.15;
export const DEFAULT_FRAMING = Object.freeze({ x: 50, y: 50, zoom: 1 });

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
const num = (v, fallback) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/** Porta qualunque valore (anche corrotto) in un'inquadratura valida. */
export function normalizeFraming(f) {
  if (!f || typeof f !== 'object') return { ...DEFAULT_FRAMING };
  return {
    x: clamp(num(f.x, 50), 0, 100),
    y: clamp(num(f.y, 50), 0, 100),
    zoom: clamp(num(f.zoom, 1), MIN_ZOOM, MAX_ZOOM),
  };
}

export function isDefaultFraming(f) {
  const n = normalizeFraming(f);
  return Math.abs(n.x - 50) < 0.05 && Math.abs(n.y - 50) < 0.05 && Math.abs(n.zoom - 1) < 0.005;
}

/** Inquadratura valida per questo veicolo (default se riferita ad un'altra foto). */
export function getCardFraming(car) {
  const cover = car && Array.isArray(car.immagini) ? car.immagini[0] : null;
  const f = car && car.cardFraming;
  if (!cover || !f || f.url !== cover) return { ...DEFAULT_FRAMING };
  return normalizeFraming(f);
}

/** Stile CSS per l'<img> (object-fit: cover già applicato dalle classi). */
export function framingImageStyle(f) {
  const n = normalizeFraming(f);
  const pos = `${n.x}% ${n.y}%`;
  return {
    objectPosition: pos,
    transformOrigin: pos,
    transform: n.zoom > 1 ? `scale(${n.zoom})` : undefined,
  };
}

// La miniatura è `${percorso}_thumb` della foto intera (imageToStorage.js):
// la usiamo solo se corrisponde davvero a quella foto, altrimenti si userebbe
// l'immagine sbagliata quando gli array immagini/miniature non sono allineati.
function isThumbOf(thumb, full) {
  if (!thumb || !full) return false;
  return thumb.split('?')[0] === `${full.split('?')[0]}_thumb`;
}

/** Sorgente da scaricare: miniatura (leggera) se basta, foto intera se zoomata. */
export function pickImageSrc(full, thumb, zoom = 1) {
  if (!full) return thumb || '';
  if (zoom > FULL_RES_ZOOM) return full;
  return isThumbOf(thumb, full) ? thumb : full;
}

export function getCardImageSrc(car) {
  const full = car && Array.isArray(car.immagini) ? car.immagini[0] : null;
  const thumb = car && Array.isArray(car.immaginiThumb) ? car.immaginiThumb[0] : null;
  return pickImageSrc(full, thumb, getCardFraming(car).zoom);
}

/**
 * Valore da salvare nel database: null se è l'inquadratura di default o se si
 * riferisce a una foto che non è (più) la copertina.
 */
export function buildFramingToSave(framing, coverUrl) {
  if (!framing || !coverUrl) return null;
  if (framing.url && framing.url !== coverUrl) return null;
  const n = normalizeFraming(framing);
  if (isDefaultFraming(n)) return null;
  return {
    url: coverUrl,
    x: Math.round(n.x * 10) / 10,
    y: Math.round(n.y * 10) / 10,
    zoom: Math.round(n.zoom * 100) / 100,
  };
}
